import { useState, useMemo, RefObject } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { BLOG_ARTICLES, BLOG_CATEGORIES } from '@/mocks/blog';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { useStaggerReveal } from '@/hooks/useScrollReveal';
import { getAllArticleViews, formatViews } from '@/hooks/useArticleViews';

const ACCENT = '#ff2d78';

const CATEGORY_COLORS: Record<string, string> = {
  'Иммунитет': '#ff2d78',
  'Уход за кожей': '#ff6b00',
  'Витамины и БАД': '#00f5ff',
  'Здоровый сон': '#39ff14',
  'Простуда и грипп': '#f0ff00',
  'Сердце и сосуды': '#ff2d78',
  'Пищеварение': '#39ff14',
  'Детское здоровье': '#00f5ff',
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

const CATEGORY_SEO: Record<string, { title: string; description: string; keywords: string }> = {
  all: {
    title: 'Блог о здоровье — статьи врачей и фармацевтов | NeonPharma',
    description: 'Статьи о здоровье от врачей NeonPharma: иммунитет, витамины, уход за кожей, здоровый сон, питание для сердца. Доказательная медицина простым языком. 12+ экспертных материалов.',
    keywords: 'здоровье статьи, витамины, иммунитет, уход за кожей, здоровый образ жизни, доказательная медицина',
  },
  immunity: {
    title: 'Иммунитет — статьи врачей | Блог NeonPharma',
    description: 'Как укрепить иммунитет: витамины, адаптогены, режим сна и питание. Статьи врачей-терапевтов NeonPharma с доказательной базой. Практические советы для здоровья.',
    keywords: 'укрепить иммунитет, витамины для иммунитета, адаптогены, иммунная система, профилактика ОРВИ',
  },
  skincare: {
    title: 'Уход за кожей — советы дерматолога | Блог NeonPharma',
    description: 'Уход за кожей лица и тела: витамины, дермакосметика, сезонный уход. Советы дерматолога-косметолога NeonPharma. Научно обоснованные рекомендации.',
    keywords: 'уход за кожей, дерматолог советы, витамины для кожи, дермакосметика, увлажнение кожи',
  },
  vitamins: {
    title: 'Витамины и БАД — как выбрать и принимать | Блог NeonPharma',
    description: 'Всё о витаминах и БАД: омега-3, коллаген, антибиотики. Как выбрать качественный препарат и правильно принимать. Статьи врачей с доказательной базой.',
    keywords: 'витамины БАД, омега-3 польза, коллаген таблетки, как принимать витамины, выбрать БАД',
  },
  heart: {
    title: 'Сердце и сосуды — питание и профилактика | Блог NeonPharma',
    description: 'Питание для здорового сердца, профилактика сердечно-сосудистых заболеваний. Статьи кардиологов и терапевтов NeonPharma. Снизьте риск инфаркта на 80%.',
    keywords: 'здоровое сердце питание, профилактика инфаркта, холестерин, сердечно-сосудистые заболевания',
  },
  cold: {
    title: 'Простуда и грипп — профилактика и лечение | Блог NeonPharma',
    description: 'Профилактика и лечение простуды и гриппа: что реально работает. Статьи врача-инфекциониста NeonPharma. Доказательная медицина против народных мифов.',
    keywords: 'профилактика простуды, лечение гриппа, ОРВИ, противовирусные препараты, вакцинация',
  },
  sleep: {
    title: 'Здоровый сон — советы сомнолога | Блог NeonPharma',
    description: 'Как наладить здоровый сон: мелатонин, циркадные ритмы, режим. Советы сомнолога и невролога NeonPharma. Научно обоснованные методы улучшения качества сна.',
    keywords: 'здоровый сон, мелатонин, нарушение сна, циркадные ритмы, как высыпаться',
  },
  digestion: {
    title: 'Пищеварение и кишечник — пробиотики и микробиом | Блог NeonPharma',
    description: 'Здоровье кишечника: пробиотики, микробиом, синдром раздражённого кишечника. Статьи врача-инфекциониста NeonPharma. Как выбрать пробиотик и когда принимать.',
    keywords: 'пробиотики, здоровье кишечника, микробиом, пищеварение, синдром раздражённого кишечника',
  },
  children: {
    title: 'Детское здоровье — витамины для детей | Блог NeonPharma',
    description: 'Витамины для детей по возрастам: витамин D, йод, железо. Советы педиатра NeonPharma. Что нужно ребёнку от 1 года до 14 лет для здоровья и развития.',
    keywords: 'витамины для детей, детское здоровье, витамин D для детей, педиатр советы, детский иммунитет',
  },
};

const buildBlogJsonLd = (activeCategory: string, articles: typeof BLOG_ARTICLES) => {
  const base = `${SITE_URL}/blog`;
  const url = activeCategory !== 'all' ? `${base}?category=${activeCategory}` : base;
  const catLabel = BLOG_CATEGORIES.find(c => c.slug === activeCategory)?.label ?? 'Все статьи';

  const breadcrumbs = [
    { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Блог', item: base },
    ...(activeCategory !== 'all' ? [{ '@type': 'ListItem', position: 3, name: catLabel, item: url }] : []),
  ];

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: activeCategory !== 'all' ? `${catLabel} — Блог NeonPharma` : 'Блог о здоровье — NeonPharma',
      description: CATEGORY_SEO[activeCategory]?.description ?? CATEGORY_SEO.all.description,
      url,
      publisher: {
        '@type': 'Organization',
        name: 'NeonPharma',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
      },
      blogPost: articles.slice(0, 10).map(a => ({
        '@type': 'BlogPosting',
        headline: a.title,
        description: a.excerpt,
        image: a.image,
        url: `${SITE_URL}/blog/${a.slug}`,
        datePublished: a.date,
        author: { '@type': 'Person', name: a.author, jobTitle: a.authorRole },
        keywords: a.tags.join(', '),
        articleSection: a.category,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: activeCategory !== 'all' ? `Статьи: ${catLabel}` : 'Все статьи блога NeonPharma',
      numberOfItems: articles.length,
      itemListElement: articles.slice(0, 10).map((a, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: a.title,
        url: `${SITE_URL}/blog/${a.slug}`,
      })),
    },
  ];
};

/* ── Article card ── */
const ArticleCard = ({
  article,
  featured = false,
  isVisible = true,
  delay = 0,
  views = 0,
  activeTag = '',
  onTagClick,
}: {
  article: typeof BLOG_ARTICLES[0];
  featured?: boolean;
  isVisible?: boolean;
  delay?: number;
  views?: number;
  activeTag?: string;
  onTagClick?: (tag: string) => void;
}) => {
  const color = CATEGORY_COLORS[article.category] ?? ACCENT;

  if (featured) {
    return (
      <Link
        to={`/blog/${article.slug}`}
        className="group relative block rounded-2xl overflow-hidden cursor-pointer col-span-1 md:col-span-2"
        style={{ border: `1px solid ${color}20`, minHeight: '340px' }}
      >
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,10,0.97) 0%, rgba(5,5,10,0.6) 50%, rgba(5,5,10,0.2) 100%)' }} />

        {/* Featured badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span
            className="px-3 py-1 rounded-full font-mono text-xs font-bold"
            style={{ background: color, color: '#050508', boxShadow: `0 0 12px ${color}80` }}
          >
            ГЛАВНАЯ СТАТЬЯ
          </span>
          <span
            className="px-2.5 py-1 rounded-full font-mono text-xs"
            style={{ background: 'rgba(5,5,10,0.7)', color: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {article.readTime} мин
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-7">
          <span
            className="inline-block px-2.5 py-0.5 rounded-full font-mono text-xs mb-3"
            style={{ background: `${color}18`, color, border: `1px solid ${color}35` }}
          >
            {article.category}
          </span>
          <h2
            className="font-orbitron font-black text-xl md:text-2xl text-white leading-tight mb-3 transition-colors duration-200 group-hover:opacity-90"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
          >
            {article.title}
          </h2>
          <p className="font-mono text-xs text-white/45 line-clamp-2 mb-4 max-w-lg">{article.excerpt}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center font-orbitron font-bold text-xs"
                style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
              >
                {article.author.charAt(0)}
              </div>
              <div>
                <p className="font-mono text-xs text-white/70">{article.author}</p>
                <p className="font-mono text-[10px] text-white/30">{article.authorRole}</p>
              </div>
            </div>
            <span
              className="flex items-center gap-1.5 font-mono text-xs transition-all duration-200 group-hover:gap-2.5"
              style={{ color }}
            >
              Читать статью
              <i className="ri-arrow-right-line" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex flex-col rounded-xl overflow-hidden cursor-pointer"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${color}12`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms, border-color 0.3s ease`,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.borderColor = `${color}35`;
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.borderColor = `${color}12`;
        (e.currentTarget as HTMLElement).style.transform = isVisible ? 'translateY(0)' : 'translateY(28px)';
      }}
    >
      <div className="relative overflow-hidden" style={{ height: '180px' }}>
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,10,0.65) 0%, transparent 60%)' }} />
        <div className="absolute top-3 left-3">
          <span
            className="px-2.5 py-1 rounded-full font-mono text-[10px]"
            style={{ background: 'rgba(5,5,10,0.75)', color, border: `1px solid ${color}35`, backdropFilter: 'blur(8px)' }}
          >
            {article.category}
          </span>
        </div>
        <div className="absolute bottom-3 right-3 flex items-center gap-1 font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <i className="ri-time-line" />
          {article.readTime} мин
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-2 line-clamp-2 group-hover:opacity-80 transition-opacity">
          {article.title}
        </h3>
        <p className="font-mono text-xs text-white/35 line-clamp-2 flex-1 mb-3 leading-relaxed">
          {article.excerpt}
        </p>

        <div className="flex flex-wrap gap-1 mb-3">
          {article.tags.slice(0, 3).map(tag => {
            const isTagActive = activeTag === tag;
            return (
              <button
                key={tag}
                onClick={e => { e.preventDefault(); onTagClick?.(isTagActive ? '' : tag); }}
                aria-pressed={isTagActive}
                className="px-2 py-0.5 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200 whitespace-nowrap"
                style={
                  isTagActive
                    ? { background: '#f0ff00', color: '#050508', boxShadow: '0 0 6px rgba(240,255,0,0.35)' }
                    : { background: 'rgba(0,245,255,0.05)', color: 'rgba(0,245,255,0.45)', border: '1px solid rgba(0,245,255,0.1)' }
                }
                onMouseEnter={e => { if (!isTagActive) (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.1)'; }}
                onMouseLeave={e => { if (!isTagActive) (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.05)'; }}
              >
                #{tag}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center font-orbitron font-bold"
              style={{ background: `${color}18`, color, fontSize: '9px' }}
            >
              {article.author.charAt(0)}
            </div>
            <span className="font-mono text-[10px] text-white/30">{formatDate(article.date)}</span>
          </div>
          <div className="flex items-center gap-3">
            {views > 0 && (
              <span className="flex items-center gap-1 font-mono text-[10px]" style={{ color: 'rgba(255,45,120,0.6)' }}>
                <i className="ri-eye-line" style={{ fontSize: '10px' }} />
                {formatViews(views)}
              </span>
            )}
            <span
              className="font-mono text-[10px] flex items-center gap-1 transition-all duration-200 group-hover:gap-1.5"
              style={{ color }}
            >
              Читать
              <i className="ri-arrow-right-line" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ── Sidebar: popular articles ── */
const SidebarPopular = () => {
  const popular = BLOG_ARTICLES.slice(0, 5);
  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(to bottom, ${ACCENT}, transparent)` }} />
        <span className="font-orbitron font-bold text-sm text-white">Популярное</span>
      </div>
      <div className="space-y-4">
        {popular.map((a, i) => {
          const color = CATEGORY_COLORS[a.category] ?? ACCENT;
          return (
            <Link
              key={a.id}
              to={`/blog/${a.slug}`}
              className="flex gap-3 group cursor-pointer"
            >
              <span
                className="font-orbitron font-black text-2xl leading-none shrink-0 w-7"
                style={{ color: 'rgba(255,255,255,0.08)' }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-orbitron font-bold text-xs text-white/70 leading-snug line-clamp-2 group-hover:text-white transition-colors mb-1">
                  {a.title}
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px]" style={{ color }}>{a.category}</span>
                  <span className="font-mono text-[10px] text-white/25">{a.readTime} мин</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ── Sidebar: categories ── */
const SidebarCategories = ({ active, onSelect }: { active: string; onSelect: (s: string) => void }) => (
  <div
    className="rounded-2xl p-5"
    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
  >
    <div className="flex items-center gap-2 mb-4">
      <div className="w-1 h-5 rounded-full" style={{ background: 'linear-gradient(to bottom, #00f5ff, transparent)' }} />
      <span className="font-orbitron font-bold text-sm text-white">Категории</span>
    </div>
    <div className="space-y-1">
      {BLOG_CATEGORIES.map(cat => {
        const count = cat.slug === 'all'
          ? BLOG_ARTICLES.length
          : BLOG_ARTICLES.filter(a => a.categorySlug === cat.slug).length;
        const isActive = active === cat.slug;
        const color = CATEGORY_COLORS[cat.label] ?? ACCENT;
        return (
          <button
            key={cat.slug}
            onClick={() => onSelect(cat.slug)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 text-left"
            style={{
              background: isActive ? `${color}12` : 'transparent',
              border: isActive ? `1px solid ${color}30` : '1px solid transparent',
            }}
          >
            <span
              className="font-mono text-xs"
              style={{ color: isActive ? color : 'rgba(255,255,255,0.45)' }}
            >
              {cat.label}
            </span>
            <span
              className="font-mono text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                background: isActive ? `${color}20` : 'rgba(255,255,255,0.05)',
                color: isActive ? color : 'rgba(255,255,255,0.25)',
              }}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

/* ── Sidebar: tag cloud ── */
const SidebarTags = ({ activeTag, onSelect }: { activeTag: string; onSelect: (t: string) => void }) => {
  const allTags = useMemo(() => {
    const counts: Record<string, number> = {};
    BLOG_ARTICLES.forEach(a => a.tags.forEach(t => { counts[t] = (counts[t] ?? 0) + 1; }));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, []);

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-5 rounded-full" style={{ background: 'linear-gradient(to bottom, #f0ff00, transparent)' }} />
        <span className="font-orbitron font-bold text-sm text-white">Теги</span>
        {activeTag && (
          <button
            onClick={() => onSelect('')}
            className="ml-auto font-mono text-[10px] cursor-pointer transition-colors"
            style={{ color: 'rgba(255,45,120,0.7)' }}
          >
            сбросить
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allTags.map(([tag, count]) => {
          const isActive = activeTag === tag;
          return (
            <button
              key={tag}
              onClick={() => onSelect(isActive ? '' : tag)}
              aria-pressed={isActive}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200 whitespace-nowrap"
              style={
                isActive
                  ? { background: '#f0ff00', color: '#050508', boxShadow: '0 0 8px rgba(240,255,0,0.4)' }
                  : { background: 'rgba(240,255,0,0.05)', color: 'rgba(240,255,0,0.5)', border: '1px solid rgba(240,255,0,0.12)' }
              }
            >
              #{tag}
              <span
                className="font-mono text-[9px] opacity-60"
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── Stats bar ── */
const StatsBar = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
    {[
      { icon: 'ri-article-line', value: `${BLOG_ARTICLES.length}+`, label: 'Статей', color: '#ff2d78' },
      { icon: 'ri-user-heart-line', value: '4', label: 'Эксперта', color: '#00f5ff' },
      { icon: 'ri-eye-line', value: '120K+', label: 'Читателей', color: '#39ff14' },
      { icon: 'ri-bookmark-line', value: '8', label: 'Категорий', color: '#f0ff00' },
    ].map((s, i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-4 rounded-xl"
        style={{ background: `${s.color}07`, border: `1px solid ${s.color}18` }}
      >
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
          style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}
        >
          <i className={s.icon} style={{ color: s.color, fontSize: '18px' }} />
        </div>
        <div>
          <p className="font-orbitron font-black text-lg leading-none" style={{ color: s.color }}>{s.value}</p>
          <p className="font-mono text-[10px] text-white/35 mt-0.5">{s.label}</p>
        </div>
      </div>
    ))}
  </div>
);

/* ── Main page ── */
const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTag, setActiveTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 6;

  const filtered = useMemo(() => {
    let result = BLOG_ARTICLES;
    if (activeCategory !== 'all') {
      result = result.filter(a => a.categorySlug === activeCategory);
    }
    if (activeTag) {
      result = result.filter(a => a.tags.includes(activeTag));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, activeTag, searchQuery]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const featured = BLOG_ARTICLES[0];

  const resetKey = `${activeCategory}-${activeTag}-${searchQuery}-${currentPage}`;
  const { containerRef, visibleItems } = useStaggerReveal(paginated.length, { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }, resetKey);

  // Get views from localStorage for all articles
  const viewsMap = useMemo(() => getAllArticleViews(), [resetKey]);

  const handleCategory = (slug: string) => {
    setActiveCategory(slug);
    setActiveTag('');
    setCurrentPage(1);
  };

  const handleTag = (tag: string) => {
    setActiveTag(tag);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchQuery('');
    setActiveCategory('all');
    setActiveTag('');
    setCurrentPage(1);
  };

  const hasFilters = searchQuery || activeCategory !== 'all' || activeTag;

  const seo = CATEGORY_SEO[activeCategory] ?? CATEGORY_SEO.all;
  const canonicalPath = activeCategory !== 'all' ? `/blog?category=${activeCategory}` : '/blog';
  const featuredOgImage = BLOG_ARTICLES[0]?.image;

  return (
    <>
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={canonicalPath}
        ogImage={featuredOgImage}
        jsonLd={buildBlogJsonLd(activeCategory, filtered)}
      />

      <div className="min-h-screen" style={{ background: '#05050a' }}>
        <Navbar />

        {/* ── Hero ── */}
        <header className="relative pt-28 pb-14 overflow-hidden">
          {/* BG glows */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-15" style={{ background: ACCENT, filter: 'blur(120px)' }} />
            <div className="absolute top-20 left-1/4 w-48 h-48 rounded-full opacity-10" style={{ background: '#00f5ff', filter: 'blur(80px)' }} />
            <div className="absolute top-10 right-1/4 w-32 h-32 rounded-full opacity-8" style={{ background: '#39ff14', filter: 'blur(60px)' }} />
          </div>

          <div className="px-6 lg:px-12 relative z-10">
            <div className="max-w-7xl mx-auto">
              {/* Breadcrumb */}
              <nav aria-label="Хлебные крошки" className="flex items-center gap-2 font-mono text-xs text-white/30 mb-8">
                <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
                <i className="ri-arrow-right-s-line" />
                {activeCategory !== 'all' ? (
                  <>
                    <Link to="/blog" className="hover:text-white/60 transition-colors">Блог</Link>
                    <i className="ri-arrow-right-s-line" />
                    <span style={{ color: ACCENT }}>{BLOG_CATEGORIES.find(c => c.slug === activeCategory)?.label}</span>
                  </>
                ) : (
                  <span style={{ color: ACCENT }}>Блог</span>
                )}
              </nav>

              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                <div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 font-mono text-xs tracking-widest"
                    style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.25)', color: ACCENT }}
                  >
                    <i className="ri-heart-pulse-line" />
                    БЛОГ О ЗДОРОВЬЕ
                  </div>
                  <h1
                    className="font-orbitron font-black text-4xl md:text-5xl text-white leading-tight"
                    style={{ textShadow: `0 0 40px ${ACCENT}30` }}
                  >
                    Статьи от<br />
                    <span style={{ color: ACCENT }}>врачей</span> и экспертов
                  </h1>
                  <p className="font-mono text-sm text-white/40 mt-4 max-w-lg leading-relaxed">
                    Доказательная медицина простым языком. Иммунитет, витамины, уход за кожей, здоровый сон — всё, что нужно знать о здоровье.
                  </p>
                </div>

                {/* Search */}
                <div className="relative w-full lg:w-80 shrink-0">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                    <i className="ri-search-line text-sm" style={{ color: 'rgba(255,255,255,0.3)' }} />
                  </div>
                  <input
                    type="search"
                    placeholder="Поиск по статьям..."
                    value={searchQuery}
                    aria-label="Поиск по статьям блога"
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl font-mono text-sm text-white outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: `1px solid ${searchQuery ? `${ACCENT}50` : 'rgba(255,255,255,0.1)'}`,
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      aria-label="Очистить поиск"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-white/30 hover:text-white/60 transition-colors"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="px-6 lg:px-12 pb-20">
          <div className="max-w-7xl mx-auto">

            {/* Stats */}
            <StatsBar />

            {/* Featured article */}
            {activeCategory === 'all' && !searchQuery && !activeTag && (
              <section aria-label="Рекомендуемая статья" className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(to bottom, ${ACCENT}, transparent)` }} />
                  <h2 className="font-orbitron font-bold text-sm text-white">Рекомендуем прочитать</h2>
                </div>
                <ArticleCard article={featured} featured />
              </section>
            )}

            {/* Main layout: grid + sidebar */}
            <div className="flex flex-col lg:flex-row gap-8">

              {/* Left: articles */}
              <div className="flex-1 min-w-0">
                {/* Category tabs */}
                <nav aria-label="Категории блога" className="flex flex-wrap gap-2 mb-4">
                  {BLOG_CATEGORIES.map(cat => {
                    const isActive = activeCategory === cat.slug;
                    const color = CATEGORY_COLORS[cat.label] ?? ACCENT;
                    return (
                      <button
                        key={cat.slug}
                        onClick={() => handleCategory(cat.slug)}
                        aria-pressed={isActive}
                        aria-label={`Фильтр: ${cat.label}`}
                        className="whitespace-nowrap px-4 py-2 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                        style={
                          isActive
                            ? { background: color, color: '#050508', boxShadow: `0 0 12px ${color}50` }
                            : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }
                        }
                      >
                        {cat.label}
                      </button>
                    );
                  })}
                </nav>

                {/* Active tag indicator */}
                {activeTag && (
                  <div className="flex items-center gap-2 mb-4">
                    <span className="font-mono text-xs text-white/30">Тег:</span>
                    <span
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(240,255,0,0.1)', color: '#f0ff00', border: '1px solid rgba(240,255,0,0.25)' }}
                    >
                      <i className="ri-price-tag-3-line" style={{ fontSize: '11px' }} />
                      #{activeTag}
                      <button
                        onClick={() => handleTag('')}
                        aria-label="Убрать фильтр по тегу"
                        className="ml-0.5 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                      >
                        <i className="ri-close-line" style={{ fontSize: '11px' }} />
                      </button>
                    </span>
                  </div>
                )}

                {/* Results count */}
                {hasFilters && (
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-mono text-xs text-white/35">
                      Найдено: <span className="text-white/70">{filtered.length}</span> статей
                    </span>
                    <button
                      onClick={handleReset}
                      className="font-mono text-xs cursor-pointer transition-colors"
                      style={{ color: `${ACCENT}cc` }}
                    >
                      Сбросить всё
                    </button>
                  </div>
                )}

                {/* Grid */}
                {paginated.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-2xl"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      <i className="ri-price-tag-3-line text-3xl text-white/15" />
                    </div>
                    <p className="font-mono text-sm text-white/30">Статьи не найдены</p>
                    <button
                      onClick={handleReset}
                      className="font-mono text-xs cursor-pointer"
                      style={{ color: `${ACCENT}cc` }}
                    >
                      Показать все статьи
                    </button>
                  </div>
                ) : (
                  <section
                    ref={containerRef as RefObject<HTMLElement>}
                    aria-label="Список статей"
                    className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  >
                    {paginated.map((article, idx) => (
                      <ArticleCard
                        key={`${article.id}-${resetKey}`}
                        article={article}
                        isVisible={visibleItems[idx]}
                        delay={idx * 80}
                        views={viewsMap[article.slug] ?? 0}
                        activeTag={activeTag}
                        onTagClick={handleTag}
                      />
                    ))}
                  </section>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav aria-label="Пагинация блога" className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      aria-label="Предыдущая страница"
                      className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all disabled:opacity-30"
                      style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        aria-label={`Страница ${p}`}
                        aria-current={currentPage === p ? 'page' : undefined}
                        className="w-9 h-9 flex items-center justify-center rounded-lg font-mono text-sm cursor-pointer transition-all"
                        style={{
                          background: currentPage === p ? ACCENT : 'transparent',
                          color: currentPage === p ? '#fff' : 'rgba(255,255,255,0.4)',
                          border: currentPage === p ? `1px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)',
                          boxShadow: currentPage === p ? `0 0 10px ${ACCENT}40` : 'none',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      aria-label="Следующая страница"
                      className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all disabled:opacity-30"
                      style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </nav>
                )}
              </div>

              {/* Right: sidebar */}
              <aside className="w-full lg:w-72 shrink-0" aria-label="Боковая панель блога">
                <div className="sticky top-24 flex flex-col gap-5 max-h-[calc(100vh-7rem)] overflow-y-auto pr-1">
                  <SidebarPopular />
                  <SidebarCategories active={activeCategory} onSelect={handleCategory} />
                  <SidebarTags activeTag={activeTag} onSelect={handleTag} />

                  {/* Newsletter CTA */}
                  <div
                    className="rounded-2xl p-5"
                    style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}25` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ background: `${ACCENT}18`, border: `1px solid ${ACCENT}35` }}>
                      <i className="ri-mail-send-line text-lg" style={{ color: ACCENT }} />
                    </div>
                    <p className="font-orbitron font-bold text-sm text-white mb-1">Рассылка о здоровье</p>
                    <p className="font-mono text-xs text-white/35 mb-4 leading-relaxed">Новые статьи от врачей каждую неделю</p>
                    <input
                      type="email"
                      placeholder="ваш@email.ru"
                      aria-label="Email для подписки на рассылку"
                      className="w-full px-3 py-2.5 rounded-lg font-mono text-xs text-white outline-none mb-2"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                    <button
                      className="whitespace-nowrap w-full py-2.5 rounded-lg font-mono text-xs cursor-pointer transition-all duration-300"
                      style={{ background: ACCENT, color: '#050508', boxShadow: `0 0 12px ${ACCENT}50` }}
                    >
                      Подписаться
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;
